import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Check, CheckCircle2, Circle, Download, ExternalLink, FileSignature, Loader2, Plus, ShoppingCart, Trash2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from './Button';
import {
  dienPvaIn,
  downloadUwvDossier,
  getPvaPrefill,
  rondPoortwachterMijlpaalAf,
  type PoortwachterCase,
  type PoortwachterDeadline,
  type PvaDoel,
  type PvaInvoer,
} from '../services/lopendeTrajectenApi';
import { ZipWachtwoordDialoog } from './ZipWachtwoordDialoog';

/**
 * Poortwachter-tijdlijn (Tabel 2) — de 7 wettelijke mijlpalen van één case,
 * met per mijlpaal de bijbehorende actie:
 *
 *   wk 6   blokkade tot de Probleemanalyse er is
 *   wk 8   vooringevuld Plan van Aanpak → ondertekening via SMS-2FA
 *   +42 d  evaluatie-vinklijst
 *   wk 42  rode grendel + poortlink naar het UWV
 *   wk 52  inkoop arbeidsdeskundige (licht op)
 *   wk 87  ZIP-dossier voor de UWV-audit
 */

const QK_POORTWACHTER = ['lopende-trajecten', 'poortwachter'];
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

export const INKOOP_ARBEIDSDESKUNDIGE_LINK = '/employer/pool-uitnodigingen?discipline=arbeidsdeskundige';

/** Is de wk 42-grendel / wk 52-inkoop / wk 87-export op dit moment aan de orde? */
export const actiefSignaal = (c: PoortwachterCase, key: PoortwachterDeadline['key']) =>
  c.status !== 'CLOSED' && c.deadlines.some((d) => d.key === key && d.signaal_actief);

// ─── Week 8: Plan van Aanpak ──────────────────────────────────────────────────

const LEGE_AFSPRAAK: PvaInvoer['afspraken'][number] = { actie: '', wie: 'werkgever', wanneer: '' };

const PvaFormulier: React.FC<{ c: PoortwachterCase; onSluit: () => void }> = ({ c, onSluit }) => {
  const qc = useQueryClient();
  const { data: p, isLoading } = useQuery({ queryKey: [...QK_POORTWACHTER, 'pva', c.id], queryFn: () => getPvaPrefill(c.id) });
  const [form, setForm] = useState<PvaInvoer>({
    reintegratiedoel: 'eigen_werk',
    toelichting_doel: '',
    advies_bedrijfsarts: '',
    afspraken: [{ ...LEGE_AFSPRAAK }],
    casemanager: '',
    evaluatie_frequentie_weken: 6,
  });
  const indienM = useMutation({
    mutationFn: () => dienPvaIn(c.id, form),
    onSuccess: () => {
      toast.success('Plan van Aanpak getekend namens de werkgever; de medewerker ontvangt een SMS om te tekenen met de 6-cijferige code');
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
      onSluit();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Indienen mislukt')),
  });
  const zetAfspraak = (i: number, patch: Partial<PvaInvoer['afspraken'][number]>) =>
    setForm((f) => ({ ...f, afspraken: f.afspraken.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const compleet =
    form.toelichting_doel.trim() && form.advies_bedrijfsarts.trim() && form.casemanager.trim() && form.afspraken.every((a) => a.actie.trim() && a.wanneer);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label="Plan van Aanpak">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-blue-600" /> Plan van Aanpak · {c.case_code}
          </h3>
          <button type="button" onClick={onSluit} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isLoading || !p ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Vooringevuld door het platform */}
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs">
              {[
                ['Werkgever', p.werkgever],
                ['Case', `${p.case_code}${p.pseudoniem ? ` · ${p.pseudoniem}` : ''}`],
                ['Vestiging / afdeling', `${p.vestiging ?? '—'} / ${p.afdeling ?? '—'}`],
                ['Eerste ziektedag', fmtDate(p.eerste_ziektedag)],
                ['Bedrijfsarts', p.bedrijfsarts],
                ['Probleemanalyse d.d.', fmtDate(p.probleemanalyse_op)],
                ['Wettelijke deadline PvA', fmtDate(p.deadline)],
              ].map(([k, v]) => (
                <div key={k as string}>
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="font-medium text-gray-900">{v || '—'}</dd>
                </div>
              ))}
            </dl>

            {p.ingediend && (
              <div className={`p-3 rounded-lg text-sm border ${p.ingediend.status === 'ondertekend' ? 'bg-green-50 border-green-200 text-green-900' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
                {p.ingediend.status === 'ondertekend'
                  ? `Tweezijdig ondertekend (SMS-2FA) op ${fmtDate(p.ingediend.ondertekend_op)}. Wijzigingen lopen via een bijstelling.`
                  : `Getekend door de werkgever op ${fmtDate(p.ingediend.werkgever_ondertekend_op ?? p.ingediend.vrijgegeven_op)}${p.ingediend.sms_verzonden_op ? ` · SMS verstuurd ${fmtDate(p.ingediend.sms_verzonden_op)}` : ''} — wacht op de SMS-ondertekening door de medewerker. Opnieuw indienen overschrijft deze versie.`}
              </div>
            )}
            {!p.kan_indienen && p.ingediend?.status !== 'ondertekend' && (
              <div className="p-3 rounded-lg text-sm bg-orange-50 border border-orange-200 text-orange-900">
                Het Plan van Aanpak volgt op de Probleemanalyse. Zodra de bedrijfsarts die heeft aangeleverd, kunt u indienen.
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass} htmlFor="pva-doel">Re-integratiedoel</label>
                <select id="pva-doel" className={inputClass} value={form.reintegratiedoel} onChange={(e) => setForm((f) => ({ ...f, reintegratiedoel: e.target.value as PvaDoel }))}>
                  {(Object.keys(p.doelen) as PvaDoel[]).map((k) => <option key={k} value={k}>{p.doelen[k]}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="pva-cm">Casemanager</label>
                <input id="pva-cm" className={inputClass} value={form.casemanager} maxLength={160} onChange={(e) => setForm((f) => ({ ...f, casemanager: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="pva-toel">Toelichting op het doel</label>
              <textarea id="pva-toel" rows={3} className={inputClass} value={form.toelichting_doel} onChange={(e) => setForm((f) => ({ ...f, toelichting_doel: e.target.value }))} />
            </div>
            <div>
              <label className={labelClass} htmlFor="pva-advies">Advies bedrijfsarts — alleen functionele mogelijkheden en beperkingen, geen diagnose</label>
              <textarea id="pva-advies" rows={3} className={inputClass} value={form.advies_bedrijfsarts} onChange={(e) => setForm((f) => ({ ...f, advies_bedrijfsarts: e.target.value }))} />
            </div>

            <div>
              <div className={labelClass}>Afspraken</div>
              <div className="space-y-2">
                {form.afspraken.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input aria-label={`Afspraak ${i + 1}`} placeholder="Wat wordt er gedaan?" className={`${inputClass} col-span-12 md:col-span-6`} value={a.actie} onChange={(e) => zetAfspraak(i, { actie: e.target.value })} />
                    <select aria-label="Door wie" className={`${inputClass} col-span-5 md:col-span-3`} value={a.wie} onChange={(e) => zetAfspraak(i, { wie: e.target.value as typeof a.wie })}>
                      <option value="werkgever">Werkgever</option>
                      <option value="werknemer">Werknemer</option>
                      <option value="bedrijfsarts">Bedrijfsarts</option>
                      <option value="anders">Anders</option>
                    </select>
                    <input aria-label="Uiterlijk op" type="date" className={`${inputClass} col-span-5 md:col-span-2`} value={a.wanneer} onChange={(e) => zetAfspraak(i, { wanneer: e.target.value })} />
                    <button
                      type="button"
                      aria-label="Afspraak verwijderen"
                      disabled={form.afspraken.length === 1}
                      className="col-span-2 md:col-span-1 flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-30"
                      onClick={() => setForm((f) => ({ ...f, afspraken: f.afspraken.filter((_, j) => j !== i) }))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="mt-2 text-xs text-blue-700 hover:underline inline-flex items-center" onClick={() => setForm((f) => ({ ...f, afspraken: [...f.afspraken, { ...LEGE_AFSPRAAK }] }))}>
                <Plus className="w-3 h-3 mr-1" /> Afspraak toevoegen
              </button>
            </div>

            <div className="w-40">
              <label className={labelClass} htmlFor="pva-freq">Evalueren elke … weken (max. 6)</label>
              <input id="pva-freq" type="number" min={1} max={6} className={inputClass} value={form.evaluatie_frequentie_weken} onChange={(e) => setForm((f) => ({ ...f, evaluatie_frequentie_weken: Number(e.target.value) }))} />
            </div>

            <div className="flex items-center justify-between gap-3 pt-3 border-t">
              <p className="text-xs text-gray-500">
                U tekent direct als werkgever (tijdstip + IP); de medewerker ontvangt een SMS en tekent anoniem en rechtsgeldig met een 6-cijferige code.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={onSluit}>Annuleren</Button>
                <Button size="sm" disabled={!p.kan_indienen || !compleet || indienM.isPending} onClick={() => indienM.mutate()}>
                  <FileSignature className="w-3.5 h-3.5 mr-1" /> Teken &amp; verstuur naar medewerker
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Evaluatie-vinklijst ──────────────────────────────────────────────────────

const EvaluatieVinklijst: React.FC<{ c: PoortwachterCase; m: PoortwachterDeadline }> = ({ c, m }) => {
  const qc = useQueryClient();
  const [vinkjes, setVinkjes] = useState<Record<string, boolean>>(Object.fromEntries((m.checklist ?? []).map((i) => [i.key, i.gedaan])));
  const bewaarM = useMutation({
    mutationFn: (afronden: boolean) =>
      rondPoortwachterMijlpaalAf(c.id, m.id, { afronden, checklist: (m.checklist ?? []).map((i) => ({ key: i.key, gedaan: !!vinkjes[i.key] })) }),
    onSuccess: (_r, afronden) => {
      toast.success(afronden ? 'Evaluatie afgerond' : 'Vinklijst opgeslagen');
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Opslaan mislukt')),
  });
  const alles = (m.checklist ?? []).every((i) => vinkjes[i.key]);
  return (
    <div className="mt-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
      <ul className="space-y-1">
        {(m.checklist ?? []).map((i) => (
          <li key={i.key}>
            <label className="flex items-start gap-2 text-xs text-gray-800 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={!!vinkjes[i.key]} onChange={(e) => setVinkjes((v) => ({ ...v, [i.key]: e.target.checked }))} />
              {i.label}
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" disabled={bewaarM.isPending} onClick={() => bewaarM.mutate(false)}>Tussentijds opslaan</Button>
        <Button size="sm" disabled={!alles || bewaarM.isPending} onClick={() => bewaarM.mutate(true)}>
          <Check className="w-3.5 h-3.5 mr-1" /> Evaluatie afronden
        </Button>
      </div>
    </div>
  );
};

// ─── Tijdlijn ─────────────────────────────────────────────────────────────────

export const PoortwachterTijdlijn: React.FC<{
  c: PoortwachterCase;
  uwvUrl: string;
  arbeidsdeskundigeInPool: boolean;
  onBoekArbeidsdeskundige: () => void;
}> = ({ c, uwvUrl, arbeidsdeskundigeInPool, onBoekArbeidsdeskundige }) => {
  const qc = useQueryClient();
  const [pvaOpen, setPvaOpen] = useState(false);
  const [exporteert, setExporteert] = useState(false);
  const [zipWachtwoord, setZipWachtwoord] = useState<string | null>(null);
  const gesloten = c.status === 'CLOSED';
  const afrondM = useMutation({
    mutationFn: (m: PoortwachterDeadline) => rondPoortwachterMijlpaalAf(c.id, m.id, { afronden: true }),
    onSuccess: () => {
      toast.success('Mijlpaal afgerond');
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Afronden mislukt')),
  });
  const exportKan = c.week_nu >= c.dossier_export_vanaf_week || c.status === 'WIA_APPLICATION';
  const exporteer = async () => {
    setExporteert(true);
    try {
      setZipWachtwoord(await downloadUwvDossier(c.id, c.case_code));
      toast.success('UWV-dossier gedownload');
    } catch (e: any) {
      toast.error(apiMessage(e, 'Exporteren mislukt'));
    } finally {
      setExporteert(false);
    }
  };
  // Alleen de eerstvolgende open evaluatie krijgt een vinklijst; de rest volgt vanzelf
  const eersteOpenEvaluatie = c.deadlines.find((d) => d.key === 'periodieke_evaluatie' && d.status === 'OPEN');

  return (
    <div className="p-4 bg-gray-50 border-t border-gray-200">
      {zipWachtwoord && <ZipWachtwoordDialoog wachtwoord={zipWachtwoord} onSluit={() => setZipWachtwoord(null)} />}
      <ol className="relative border-l-2 border-gray-200 ml-2 space-y-3">
        {c.deadlines.map((m) => {
          const kritiek = m.signaal_actief && ['uwv_42e_week', 'eerstejaarsevaluatie', 'wia_aanvraag'].includes(m.key);
          const Icoon = m.status === 'AFGEROND' ? CheckCircle2 : kritiek ? AlertOctagon : Circle;
          const kleur =
            m.status === 'AFGEROND' ? 'text-green-600' : m.status === 'VERVALLEN' ? 'text-gray-300' : kritiek || m.urgentie === 'verlopen' ? 'text-red-600' : m.signaal_actief ? 'text-orange-500' : 'text-gray-400';
          return (
            <li key={m.id} className={`pl-5 relative ${kritiek ? 'p-3 rounded-lg bg-red-50 border-2 border-red-400' : ''}`}>
              <Icoon className={`w-4 h-4 absolute -left-[9px] top-1 bg-gray-50 ${kleur}`} />
              <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
                <span className="font-mono text-gray-500 w-12">wk {m.key === 'ziekmelding' ? 1 : m.week}</span>
                <span className={`font-semibold ${kritiek ? 'text-red-800' : 'text-gray-900'}`}>{m.label}</span>
                <span className="text-gray-500">
                  {fmtDate(m.datum)}
                  {m.status === 'OPEN' && ` · ${m.dagen_te_gaan >= 0 ? `nog ${m.dagen_te_gaan} d` : `${-m.dagen_te_gaan} d over tijd`}`}
                  {m.status === 'AFGEROND' && m.key !== 'ziekmelding' && ` · afgerond ${fmtDate(m.afgerond_op)}`}
                </span>
                {m.signaal_actief && m.signaal && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">{m.signaal}</span>}
              </div>

              {!gesloten && m.status === 'OPEN' && (
                <div className="mt-1.5">
                  {m.key === 'probleemanalyse' && (
                    <p className="text-xs text-orange-800">
                      Case geblokkeerd: de status kan pas verder dan PROBLEM_ANALYSIS_DUE nadat de bedrijfsarts de Probleemanalyse digitaal heeft aangeleverd.
                    </p>
                  )}
                  {m.key === 'plan_van_aanpak' && (
                    <Button size="sm" variant={m.signaal_actief ? 'primary' : 'outline'} onClick={() => setPvaOpen(true)}>
                      <FileSignature className="w-3.5 h-3.5 mr-1" /> Plan van Aanpak invullen
                    </Button>
                  )}
                  {m.key === 'periodieke_evaluatie' && m.id === eersteOpenEvaluatie?.id && m.signaal_actief && <EvaluatieVinklijst c={c} m={m} />}
                  {m.key === 'uwv_42e_week' && m.signaal_actief && (
                    <div className="flex flex-wrap items-center gap-2">
                      <a href={uwvUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700">
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Naar het UWV-werkgeversportaal
                      </a>
                      <Button size="sm" variant="outline" disabled={afrondM.isPending} onClick={() => afrondM.mutate(m)}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Gemeld bij UWV
                      </Button>
                    </div>
                  )}
                  {m.key === 'eerstejaarsevaluatie' && (
                    <div className="flex flex-wrap items-center gap-2">
                      {arbeidsdeskundigeInPool && (
                        <Button size="sm" variant={m.signaal_actief ? 'primary' : 'outline'} onClick={onBoekArbeidsdeskundige}>
                          Boek arbeidsdeskundige uit uw pool
                        </Button>
                      )}
                      <Link
                        to={INKOOP_ARBEIDSDESKUNDIGE_LINK}
                        className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                          m.signaal_actief ? 'bg-amber-400 border-amber-500 text-amber-950 ring-4 ring-amber-200 animate-pulse' : 'border-gray-300 text-gray-700 hover:bg-white'
                        }`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Koop zzp-arbeidsdeskundige in uw regio in
                      </Link>
                    </div>
                  )}
                  {m.key === 'wia_aanvraag' && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant={exportKan ? 'primary' : 'outline'} disabled={!exportKan || exporteert} title={exportKan ? undefined : `Beschikbaar vanaf week ${c.dossier_export_vanaf_week}`} onClick={exporteer}>
                        {exporteert ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                        Exporteer UWV-dossier (ZIP)
                      </Button>
                      {m.signaal_actief && (
                        <Button size="sm" variant="outline" disabled={afrondM.isPending} onClick={() => afrondM.mutate(m)}>
                          <Check className="w-3.5 h-3.5 mr-1" /> WIA-aanvraag ingediend
                        </Button>
                      )}
                      <span className="text-[11px] text-gray-500">Bundelt alle via SMS-2FA ondertekende documenten + manifest met SHA-256-controlegetallen. Elke export wordt gelogd.</span>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {pvaOpen && <PvaFormulier c={c} onSluit={() => setPvaOpen(false)} />}
    </div>
  );
};
